import { motion } from "framer-motion";
import { ExternalLink, Film, Play } from "lucide-react";
import { VIDEOS } from "@/lib/images";

export default function VideoGallery() {
  if (VIDEOS.length === 0) return null;

  return (
    <section className="bg-forest py-24 md:py-32">
      <div className="mx-auto max-w-6xl px-5">
        <div className="mb-14 text-center">
          <Film className="mx-auto h-9 w-9 text-gold" />
          <p className="mt-4 text-xs uppercase tracking-[0.3em] text-gold">Moving Memories</p>
          <h2 className="mt-3 font-serif text-4xl font-bold tracking-tight text-cream md:text-5xl">
            Agnes in <span className="italic text-gold">Motion</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl leading-relaxed text-cream/65">
            Moments that move — Agnes laughing, celebrating, and living every second of her remarkable century.
          </p>
        </div>

        <div className="grid gap-10 sm:grid-cols-2">
          {VIDEOS.map((video, i) => (
            <motion.div
              key={`${video.externalUrl || video.src || "video"}-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
            >
              {video.externalUrl ? (
                <a
                  href={video.externalUrl}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${video.externalLabel || "Open"}: ${video.title}`}
                  className="group relative block overflow-hidden rounded-xl bg-black shadow-2xl"
                >
                  <img
                    src={video.poster}
                    alt=""
                    className="aspect-video w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-forest/45 transition-colors duration-300 group-hover:bg-forest/30">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold text-forest shadow-xl transition-transform duration-300 group-hover:scale-110">
                      <Play className="ml-1 h-7 w-7 fill-current" />
                    </span>
                    <span className="flex items-center gap-2 rounded-full bg-black/45 px-4 py-2 text-sm font-medium text-cream backdrop-blur-sm">
                      {video.externalLabel || "Open feature"}
                      <ExternalLink className="h-4 w-4" />
                    </span>
                  </div>
                </a>
              ) : (
                <video
                  src={video.src}
                  poster={video.poster}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full rounded-xl shadow-2xl bg-black"
                  style={{ maxHeight: "480px" }}
                />
              )}
              <div className="mt-4 text-center">
                <p className="font-hand text-xl text-cream">{video.title}</p>
                {video.year && (
                  <p className="mt-1 text-xs uppercase tracking-widest text-gold">{video.year}</p>
                )}
                {video.source && (
                  <p className="mt-2 text-sm text-cream/65">{video.source} · {video.description}</p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
